import './main.scss';
import { type INavigationClient } from '@azure/msal-browser';
import { glsClearLocalStorageWhenAppVersionChange, glsCustomNavigationClient } from '@gls-its-frontends/common/gls-app-config';
import { glsVueQueryPluginOptions } from '@gls-its-frontends/common/gls-query';
import { glsCreateI18nInstance } from '@gls-its-frontends/common/gls-translate';
import { PermissionDirective } from '@shipit/shared/access-control/feature-access-control';
import { appProviderPlugin } from '@shipit/shared/app-instance/data-access';
import { VersionService } from '@shipit/shared/app-version/data-access';
import {
    type Environment,
    getEnvironment,
    LOCAL_STORAGE_PREFIX,
    setAppTitle,
    setUpDynatrace,
    setUpEnvironmentConfig
} from '@shipit/shared/environment/util';
import { FeatureFlagService } from '@shipit/shared/feature-flags/util';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia, type Pinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { type App as AppType, createApp, type Plugin } from 'vue';
import de from './i18n/de.json';
import en from './i18n/en.json';
import fr from './i18n/fr.json';
import pl from './i18n/pl.json';

/**
 * The app is wrapped into an async IIFE because `window.env` is only filled by
 * `setUpEnvironmentConfig`, so everything reading it at module scope (MSAL, the HTTP clients,
 * the views) must be imported after that call.
 */
(async (): Promise<void> => {
    const environment: Environment = getEnvironment();

    await FeatureFlagService.init();
    await VersionService.init();
    if (VersionService.appVersion) {
        glsClearLocalStorageWhenAppVersionChange(VersionService.appVersion, LOCAL_STORAGE_PREFIX);
    }

    setUpEnvironmentConfig(environment);
    setUpDynatrace(environment);
    setAppTitle(environment);

    const { default: App } = await import('./App.vue');
    const { default: router } = await import('./routing/router');
    const { MSAL_AD, MSAL_B2C } = await import('@shipit/shared/authentication/data-access');

    const navigationClient: INavigationClient = glsCustomNavigationClient(router);
    MSAL_AD.setNavigationClient(navigationClient);
    MSAL_B2C.setNavigationClient(navigationClient);

    const app: AppType<Element> = createApp(App);

    const pinia: Pinia = createPinia().use(piniaPluginPersistedstate);
    const i18n: Plugin = glsCreateI18nInstance({ de, en, pl, fr });

    app.directive('permission', PermissionDirective)
        .use(appProviderPlugin)
        .use(pinia)
        .use(i18n)
        .use(VueQueryPlugin, glsVueQueryPluginOptions)
        .use(router)
        .mount('#root');
})();
